import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { isDisposableEmail } from "utils/emailPolicy";
import { isValidEmailAddress } from "utils/emailValidation";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { isValidTelegram } from "utils/telegramPolicy";
import { COUNTRIES } from "constants/countries";
import { LEGAL_PAGES_LAST_UPDATED } from "legal/legalMeta";
import { SIGNUP_LEGAL_DOCUMENTS } from "legal/signupLegalDocuments";

const MIN_PASSWORD_LENGTH = 8;
const USERNAME_RE = /^[a-zA-Z0-9_]{3,32}$/;

const INITIAL_FORM = {
  nom: "",
  prenom: "",
  utilisateur: "",
  email: "",
  country: "",
  telegram: "",
  mot_de_passe: "",
  mot_de_passe_confirmation: "",
};

const INPUT_BASE =
  "mt-1 mb-2 flex h-10 w-full items-center justify-center rounded-xl bg-white/5 px-3 text-sm text-white outline-none transition-colors placeholder:text-gray-400";
const INPUT_BORDER_OK = "border border-white/20";
const INPUT_BORDER_ERR =
  "border-2 border-red-500 dark:border-red-400";

/** Trigger matches other fields; list is a custom panel (native <select> cannot style the OS dropdown on Windows). */
const COUNTRY_TRIGGER_BASE =
  "mt-1 mb-2 flex h-10 w-full cursor-pointer items-center justify-between rounded-xl bg-white/5 px-3 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand-500/40";

function countriesStartingWith(letter) {
  const u = letter.toUpperCase();
  return COUNTRIES.filter((c) => c.toUpperCase().startsWith(u));
}

const REFERRAL_STORAGE_KEY = "alphasniper_signup_ref";

export default function InscriptionClient() {
  const [searchParams] = useSearchParams();
  const referrerClientId = useMemo(() => {
    const fromUrl = String(searchParams.get("ref") || "").trim();
    if (fromUrl && /^\d+$/.test(fromUrl)) {
      try {
        sessionStorage.setItem(REFERRAL_STORAGE_KEY, fromUrl);
      } catch {
        /* ignore */
      }
      return fromUrl;
    }
    try {
      return String(sessionStorage.getItem(REFERRAL_STORAGE_KEY) || "").trim();
    } catch {
      return "";
    }
  }, [searchParams]);

  const [form, setForm] = useState(INITIAL_FORM);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const countryMenuRef = useRef(null);
  const countryListRef = useRef(null);
  const countryOptionRefs = useRef({});
  const countryLetterCycleRef = useRef({ letter: null, index: 0 });
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalChecks, setLegalChecks] = useState({
    terms: false,
    risk: false,
    privacy: false,
    refund: false,
  });
  const [activeLegalDoc, setActiveLegalDoc] = useState(null);
  const [legalError, setLegalError] = useState("");

  const emailOk = useMemo(
    () => isValidEmailAddress(form.email),
    [form.email],
  );
  const usernameOk = useMemo(
    () => USERNAME_RE.test(form.utilisateur.trim()),
    [form.utilisateur],
  );
  const emailDisposable = useMemo(
    () => (emailOk ? isDisposableEmail(form.email) : false),
    [form.email, emailOk],
  );
  const telegramOk = useMemo(
    () => isValidTelegram(form.telegram),
    [form.telegram],
  );
  const passwordLongEnough = form.mot_de_passe.length >= MIN_PASSWORD_LENGTH;
  const passwordsMatch =
    form.mot_de_passe === form.mot_de_passe_confirmation;

  const canSubmit =
    form.nom.trim() &&
    form.prenom.trim() &&
    form.utilisateur.trim() &&
    usernameOk &&
    emailOk &&
    !emailDisposable &&
    form.country.trim() &&
    form.telegram.trim() &&
    telegramOk &&
    passwordLongEnough &&
    passwordsMatch &&
    recaptchaToken;

  const errPrenom = triedSubmit && !form.prenom.trim();
  const errNom = triedSubmit && !form.nom.trim();
  const errUser =
    triedSubmit &&
    (!form.utilisateur.trim() || (form.utilisateur.trim() && !usernameOk));
  const errEmail =
    triedSubmit &&
    (!form.email.trim() || !emailOk || (emailOk && emailDisposable));
  const errCountry = triedSubmit && !form.country.trim();
  const errTelegram =
    triedSubmit &&
    (!form.telegram.trim() || (form.telegram.trim() && !telegramOk));
  const errPassword =
    triedSubmit &&
    (!form.mot_de_passe || !passwordLongEnough);
  const errConfirmPassword =
    triedSubmit &&
    (!form.mot_de_passe_confirmation || !passwordsMatch);
  const errCaptcha = triedSubmit && !recaptchaToken;

  const inputClass = (hasError) =>
    `${INPUT_BASE} ${hasError ? INPUT_BORDER_ERR : INPUT_BORDER_OK}`;

  const countryTriggerClass = (hasError) =>
    `${COUNTRY_TRIGGER_BASE} ${hasError ? INPUT_BORDER_ERR : INPUT_BORDER_OK}`;

  useEffect(() => {
    if (!countryMenuOpen) {
      countryLetterCycleRef.current = { letter: null, index: 0 };
      return;
    }
    requestAnimationFrame(() => {
      countryListRef.current?.focus({ preventScroll: true });
    });
  }, [countryMenuOpen]);

  useEffect(() => {
    if (!countryMenuOpen) return;
    const onPointerDown = (e) => {
      if (countryMenuRef.current && !countryMenuRef.current.contains(e.target)) {
        setCountryMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setCountryMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [countryMenuOpen]);

  useEffect(() => {
    if (!countryMenuOpen || !form.country) return;
    const el = countryOptionRefs.current[form.country];
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [form.country, countryMenuOpen]);

  const onCountryListKeyDown = useCallback((e) => {
    if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
      const L = e.key.toUpperCase();
      const matches = countriesStartingWith(L);
      if (matches.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      const cyc = countryLetterCycleRef.current;
      if (cyc.letter === L) {
        cyc.index = (cyc.index + 1) % matches.length;
      } else {
        cyc.letter = L;
        cyc.index = 0;
      }
      const pick = matches[cyc.index];
      setForm((p) => ({ ...p, country: pick }));
    }
  }, []);

  const onFieldChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const resetFeedback = useCallback(() => {
    setRegistrationSuccess(false);
    setErrorText("");
  }, []);

  const submitRegistration = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl("/api/inscriptions"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: form.nom.trim(),
          prenom: form.prenom.trim(),
          utilisateur: form.utilisateur.trim(),
          email: form.email.trim(),
          country: form.country.trim(),
          telegram: form.telegram.trim(),
          mot_de_passe: form.mot_de_passe,
          recaptchaToken,
          ...(referrerClientId ? { referrerClientId: Number(referrerClientId) } : {}),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        setErrorText(clientSafeMessage(data.message, "Registration failed."));
        return;
      }

      setRegistrationSuccess(true);

      try {
        sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
      } catch {
        /* ignore */
      }

      setForm(INITIAL_FORM);
      setCountryMenuOpen(false);
      setRecaptchaToken("");
      setTriedSubmit(false);
      setShowLegalModal(false);
      setLegalError("");
      setLegalChecks({
        terms: false,
        risk: false,
        privacy: false,
        refund: false,
      });
      setActiveLegalDoc(null);
    } catch {
      setErrorText("Network error. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const openLegalModal = (event) => {
    event.preventDefault();
    setTriedSubmit(true);
    resetFeedback();
    if (!canSubmit) return;
    setLegalError("");
    setActiveLegalDoc(null);
    setShowLegalModal(true);
  };

  const handleLegalDocClick = (key) => {
    setActiveLegalDoc((prev) => (prev === key ? null : key));
  };

  const handleFinalizeSignup = () => {
    if (!Object.values(legalChecks).every(Boolean)) {
      setLegalError(
        "Please read and accept all legal documents before continuing.",
      );
      return;
    }
    setLegalError("");
    submitRegistration();
  };

  if (registrationSuccess) {
    return (
      <div className="relative z-30 my-6 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:justify-start">
        <div className="w-full min-w-0 max-w-[560px] md:pl-4 lg:pl-0">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-navy-900">
            <h2 className="text-4xl font-extrabold leading-tight text-green-600 dark:text-green-400 sm:text-5xl">
            Registration successful
            </h2>
            <p className="mt-5 text-2xl font-semibold leading-snug text-navy-700 dark:text-white sm:text-[2rem]">
              We sent you an e-mail with a verification link.
            </p>
            <p className="mt-5 text-lg text-gray-600 dark:text-gray-300">
              Open the message from AlphaSniper and click{" "}
              <strong>Verify my e-mail</strong>. You need to confirm your address
              before you can sign in. Check spam if you do not see it.
            </p>
            <Link
              to="/SignIn"
              className="mt-6 inline-block text-lg font-semibold text-brand-500 underline-offset-4 hover:underline dark:text-brand-300"
            >
              Go to SignIn (after you have verified)
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-30 my-1 flex h-full w-full items-start justify-center px-2 md:mx-0 md:px-0 lg:items-start lg:justify-start">
      <div className="mt-0 flex w-full min-w-0 max-w-[560px] flex-col items-stretch md:pl-4 lg:pl-0">
        <h4 className="mb-1.5 text-center text-4xl font-bold text-white">
          Sign Up
        </h4>
        <p className="mb-4 text-center text-sm text-gray-300">
          Fill in the form to create your account.
        </p>

        <form onSubmit={openLegalModal} noValidate>
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <label className="ml-1.5 text-sm font-medium text-gray-100">
            First name*
            <input
              name="prenom"
              type="text"
              value={form.prenom}
              onChange={onFieldChange}
              placeholder="Your first name"
              autoComplete="given-name"
              className={inputClass(errPrenom)}
              aria-invalid={errPrenom}
            />
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            Last name*
            <input
              name="nom"
              type="text"
              value={form.nom}
              onChange={onFieldChange}
              placeholder="Your last name"
              autoComplete="family-name"
              className={inputClass(errNom)}
              aria-invalid={errNom}
            />
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            User: *
            <input
              name="utilisateur"
              type="text"
              value={form.utilisateur}
              onChange={onFieldChange}
              placeholder="3–32 chars: letters, numbers, _"
              autoComplete="username"
              className={inputClass(errUser)}
              aria-invalid={errUser}
            />
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            E-mail: *
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onFieldChange}
              placeholder="you@example.com"
              autoComplete="email"
              className={inputClass(errEmail)}
              aria-invalid={errEmail}
            />
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            Password: *
            <input
              name="mot_de_passe"
              type="password"
              value={form.mot_de_passe}
              onChange={onFieldChange}
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
              autoComplete="new-password"
              className={inputClass(errPassword)}
              aria-invalid={errPassword}
            />
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            Confirm password*
            <input
              name="mot_de_passe_confirmation"
              type="password"
              value={form.mot_de_passe_confirmation}
              onChange={onFieldChange}
              placeholder="Re-enter your password"
              autoComplete="new-password"
              className={inputClass(errConfirmPassword)}
              aria-invalid={errConfirmPassword}
            />
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            Country*
            <div ref={countryMenuRef} className="relative">
              <button
                type="button"
                id="signup-country-trigger"
                aria-haspopup="listbox"
                aria-expanded={countryMenuOpen}
                aria-invalid={errCountry}
                className={countryTriggerClass(errCountry)}
                onClick={() => setCountryMenuOpen((open) => !open)}
              >
                <span className={form.country ? "text-white" : "text-gray-400"}>
                  {form.country || "Select your country"}
                </span>
                <span className="text-gray-400" aria-hidden>
                  ▾
                </span>
              </button>
              {countryMenuOpen ? (
                <ul
                  ref={countryListRef}
                  role="listbox"
                  tabIndex={-1}
                  aria-labelledby="signup-country-trigger"
                  onKeyDown={onCountryListKeyDown}
                  className="absolute left-0 right-0 top-full z-[100] mt-1 max-h-60 overflow-y-auto rounded-xl border border-white/20 bg-[#0B1437] py-1 shadow-[0_16px_40px_rgba(0,0,0,0.55)] outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  <li role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={!form.country}
                      className="w-full px-3 py-2.5 text-left text-sm text-gray-400 hover:bg-white/10 hover:text-white"
                      onClick={() => {
                        countryLetterCycleRef.current = { letter: null, index: 0 };
                        setForm((p) => ({ ...p, country: "" }));
                        setCountryMenuOpen(false);
                      }}
                    >
                      Select your country
                    </button>
                  </li>
                  {COUNTRIES.map((countryName) => (
                    <li key={countryName} role="presentation">
                      <button
                        ref={(el) => {
                          if (el) countryOptionRefs.current[countryName] = el;
                          else delete countryOptionRefs.current[countryName];
                        }}
                        type="button"
                        role="option"
                        aria-selected={form.country === countryName}
                        className={`w-full px-3 py-2.5 text-left text-sm text-white hover:bg-white/10 ${
                          form.country === countryName ? "bg-white/15 font-medium" : ""
                        }`}
                        onClick={() => {
                          countryLetterCycleRef.current = { letter: null, index: 0 };
                          setForm((p) => ({ ...p, country: countryName }));
                          setCountryMenuOpen(false);
                        }}
                      >
                        {countryName}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            Telegram: *
            <input
              name="telegram"
              type="text"
              value={form.telegram}
              onChange={onFieldChange}
              placeholder="@username or +33 6 12 34 56 78"
              autoComplete="off"
              className={inputClass(errTelegram)}
              aria-invalid={errTelegram}
            />
            <span className="ml-1.5 mt-0.5 block text-[11px] leading-tight text-gray-400">
              Use your Telegram @username (5–32 characters) or the phone number
              linked to your Telegram account (with country code if needed).
            </span>
          </label>
          </div>

          <div
            className={`mb-2 mt-1 rounded-xl p-2 ${
              errCaptcha
                ? "border-2 border-red-500 dark:border-red-400"
                : "border border-white/15 bg-white/5"
            }`}
          >
            <p className="mb-2 text-center text-sm font-medium text-gray-100">
              reCAPTCHA*
            </p>
            <div className="flex justify-center">
              <div className="origin-center scale-[0.93]">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
                  onChange={(token) => setRecaptchaToken(token || "")}
                />
              </div>
            </div>
          </div>

          {triedSubmit &&
            form.utilisateur.trim() &&
            !usernameOk && (
              <p className="mb-2 text-sm text-red-400">
                Username must be 3–32 characters (letters, numbers, underscore
                only). It will be stored in lowercase.
              </p>
            )}
          {triedSubmit && emailOk && emailDisposable && (
            <p className="mb-2 text-sm text-red-400">
              Disposable or temporary e-mail addresses are not allowed. Use a
              permanent address.
            </p>
          )}
          {triedSubmit && form.telegram.trim() && !telegramOk && (
            <p className="mb-2 text-sm text-red-400">
              Enter a valid Telegram @username (5–32 characters) or a phone
              number with 8–15 digits (spaces and + allowed).
            </p>
          )}
          {triedSubmit && form.mot_de_passe && !passwordLongEnough && (
            <p className="mb-2 text-sm text-red-400">
              Password must be at least {MIN_PASSWORD_LENGTH} characters.
            </p>
          )}
          {triedSubmit &&
            form.mot_de_passe_confirmation &&
            !passwordsMatch && (
              <p className="mb-2 text-sm text-red-400">
                Passwords do not match.
              </p>
            )}

          {errorText && (
            <p className="mb-2 text-sm text-red-400">{errorText}</p>
          )}

          {triedSubmit && !canSubmit && (
            <p className="mb-2 text-sm text-red-400">
              Please fill in all fields correctly.
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="linear mt-1 w-full rounded-xl bg-brand-500 py-[10px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {loading ? "Sending…" : "SignUp"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          You have an account?{" "}
          <Link
            to="/SignIn"
            className="font-medium text-brand-300 hover:text-brand-200"
          >
            SignIn
          </Link>
        </p>

        {showLegalModal && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 px-4">
            <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0f1a4b] p-6 text-white shadow-2xl">
              <h3 className="text-xl font-bold">Legal Confirmation</h3>
              <p className="mt-2 text-sm text-gray-300">
                Read and accept each legal form to continue. The text below is built from the same
                source as our public legal pages (last updated {LEGAL_PAGES_LAST_UPDATED}) — update
                the files under <span className="font-mono text-xs text-gray-400">src/legal/</span>{" "}
                to change both the website and this modal together.
              </p>

              <div className="mt-4 space-y-3 rounded-xl border border-white/10 bg-[#0b1437] p-4">
                <div
                  className={`overflow-hidden rounded-lg border border-gray-300 bg-white text-gray-900 transition-all duration-300 ${
                    activeLegalDoc
                      ? "max-h-[260px] p-4"
                      : "max-h-0 border-transparent p-0"
                  }`}
                >
                  {activeLegalDoc && (
                    <>
                      <h4 className="text-base font-bold text-gray-900">
                        {SIGNUP_LEGAL_DOCUMENTS[activeLegalDoc].title}
                      </h4>
                      <div className="mt-3 max-h-[180px] space-y-3 overflow-auto pr-2 text-sm leading-relaxed text-gray-800">
                        {SIGNUP_LEGAL_DOCUMENTS[activeLegalDoc].body.map((paragraph, idx) => (
                          <p key={`${activeLegalDoc}-${idx}`}>{paragraph}</p>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  {Object.entries(SIGNUP_LEGAL_DOCUMENTS).map(([key, doc]) => (
                    <div
                      key={key}
                      className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                    >
                      <button
                        type="button"
                        onClick={() => handleLegalDocClick(key)}
                        className="text-left text-sm font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline"
                      >
                        {doc.title}
                      </button>

                      <label className="inline-flex items-center gap-2 text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={legalChecks[key]}
                          onChange={(e) =>
                            setLegalChecks((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                          className="h-4 w-4 rounded border-white/20 bg-white/5"
                        />
                        Accept
                      </label>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setLegalChecks({
                        terms: true,
                        risk: true,
                        privacy: true,
                        refund: true,
                      })
                    }
                    className="rounded-lg border border-brand-300/40 bg-brand-500/15 px-3 py-2 text-sm font-semibold text-brand-200 transition hover:bg-brand-500/25"
                  >
                    Accept all
                  </button>
                </div>
              </div>

              {legalError ? (
                <p className="mt-3 text-sm text-amber-300">{legalError}</p>
              ) : null}
              {errorText ? (
                <p className="mt-2 text-sm text-amber-300">{errorText}</p>
              ) : null}

              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowLegalModal(false);
                    setLegalError("");
                  }}
                  className="rounded-xl border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/5"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleFinalizeSignup}
                  disabled={loading}
                  className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "SignUp"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
